import axios from "axios";
import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import deepCopy from "../../../../utils/deepcopy.js";
import "./index.css";
import EnhancedTable from "../../components/EnhancedTable.jsx";
import {
  InputLabel,
  MenuItem,
  FormControl,
  Select,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
} from "@mui/material";
import { updateAlert, updateUsers } from "../../redux/actions.js";
import { isEmail } from "../../../../utils/isEmail.js";

function UserPanel(props) {
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [userOpen, setUserOpen] = useState(false);
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState({});
  const [searchUsers, setSearchUsers] = useState([]);
  const [userReload, setUserReload] = useState(false);

  // ---------- user info ----------
  const [role, setRole] = useState("Regular");
  const [status, setStatus] = useState("True");
  const [username, setUsername] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");

  // ---------- add user info ----------

  const [addUsername, setAddUsername] = useState("");
  const [addEmail, setAddEmail] = useState("");
  const [addRole, setAddRole] = useState("Internal")
  const [addFirstName, setAddFirstName] = useState("")
  const [addLastName, setAddLastName] = useState("")

  const userHeadCells = [
    {
      id: "id",
      numeric: true,
      disablePadding: true,
      label: "UserId",
      align: "center",
    },
    {
      id: "email",
      numeric: false,
      disablePadding: false,
      label: "Email",
      align: "left",
    },
    {
      id: "username",
      numeric: false,
      disablePadding: false,
      label: "Username",
      align: "left",
    },
    {
      id: "name",
      numeric: false,
      disablePadding: false,
      label: "Name",
      align: "center",
    },
    {
      id: "role",
      numeric: false,
      disablePadding: false,
      label: "Role",
      align: "center",
    },
    {
      id: "status",
      numeric: true,
      disablePadding: false,
      label: "Status",
      align: "center",
    },
  ];

  useEffect(() => {
    if (props.users === null || userReload) {
      axios({
        method: "post",
        url: `/api/m2d/admin/getAllUsers`,
        data: {
          adminId: props.user.id,
        },
      }).then((res) => {
        const new_users = deepCopy(res.data);
        new_users.map((user) => {
          user.status = user?.featureUsers[0]?.status;
          user.role = user?.featureUsers[0]?.role;
          user.name = `${user?.firstName} ${user?.lastName}`
          return user
        });
        setUsers(new_users);
        props.handleUsers(new_users)
        setUserReload(false)
      });
    } else {
      setUsers(props.users)
    }
  }, [userReload, props]);

  // ---------- user functions ----------

  const handleUsernameChange = (e) => {
    setUsername(e.target.value);
  };

  const handleRoleChange = (e) => {
    setRole(e.target.value);
  };

  const handleActiveChange = (e) => {
    setStatus(e.target.value);
  };

  const handleUserClose = () => {
    setUserOpen(false);
  };

  const handleUserConfirm = () => {
    if (
      selectedUser.role === role &&
      selectedUser.status === status &&
      selectedUser.firstName === firstName &&
      selectedUser.lastName === firstName
    ) {
      handleUserClose();
    } else {
      axios({
        method: "post",
        url: `/api/m2d/admin/editUser`,
        data: {
          adminId: props.user.id,
          uid: selectedUser.id,
          firstName: firstName,
          lastName: lastName,
          username: username,
          role: role,
          status: status,
        },
      })
        .then((res) => {
          if (res.data.success) {
            handleUserClose();
            setUserReload(true);
            props.handleAlert("success", "Successfully updated!");
          } else {
            // logout and exit
            props.handleLogout();
            navigate("/");
          }
        })
        .catch((err) => {
          // alert
          props.handleAlert("error", err.response.data.message, 3500);
          console.log(err)
        });
    }
  };

  const handleUserEdit = (selected) => {
    if (selected) {
      setSelectedUser(selected);
      setRole(selected.role);
      setStatus(selected.status);
      setUsername(selected.username);
      setFirstName(selected.firstName)
      setLastName(selected.lastName)
      setUserOpen(true)
    }
  };

  const handleUserSearch = (email) => {
    setSearchUsers(users.filter(user => user.email.includes(email)))
  };

  const handleClearSearch = () => {
    setSearchUsers([])
  };

  const handleAddUser = () => {
    setAddUserOpen(true)
  }

  const handleAddUserClose = () => {
    setAddUserOpen(false)
  }

  const handleAddEmailChange = (e) => {
    setAddEmail(e.target.value)
  }

  const handleAddUsernameChange = (e) => {
    setAddUsername(e.target.value)
  }

  const handleAddRoleChange = (e) => {
    setAddRole(e.target.value)
  }

  const handleAddFirstNameChange = (e) => {
    setAddFirstName(e.target.value)
  }

  const handleAddLastNameChange = (e) => {
    setAddLastName(e.target.value)
  }

  const handleEditFirstNameChange = (e) => {
    setFirstName(e.target.value)
  }

  const handleEditLastNameChange = (e) => {
    setLastName(e.target.value)
  }

  const handleAddUserConfirm = () => {
    if (!addEmail || addUsername.length < 2 || !addFirstName || !addLastName) {
      props.handleAlert("error", "Please complete the form!", 3000)
      return
    }

    if (!isEmail(addEmail)) {
      props.handleAlert("error", "Please enter a valid email address!", 3000)
      return
    }

    axios({
      method: "post",
      url: "/api/m2d/admin/addUser",
      data: {
        adminId: props.user.id,
        email: addEmail,
        username: addUsername,
        role: addRole,
        firstName: addFirstName,
        lastName: addLastName
      }
    }).then(() => {
      setAddUserOpen(false);
      setAddEmail("");
      setAddUsername("");
      setAddFirstName("");
      setAddLastName("");
      setAddRole("Internal")
      setUserReload(true)
      props.handleAlert("success", "Successfully added!");
    })

  }

  return (
    <>
      <EnhancedTable
        title="Users"
        headCells={userHeadCells}
        rows={searchUsers.length > 0 ? searchUsers : users}
        handleEdit={handleUserEdit}
        handleSearch={handleUserSearch}
        handleClearSearch={handleClearSearch}
        search="Search by email"
        handleAdd={handleAddUser}
      />
      {/* ---------- edit user ---------- */}
      <Dialog open={userOpen} onClose={handleUserClose}>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Please update {selectedUser.Username}'s information here.
          </DialogContentText>
          <TextField
            margin="normal"
            id="name"
            label="Username"
            type="username"
            fullWidth
            variant="outlined"
            value={username}
            onChange={handleUsernameChange}
          />
          <TextField
            margin="normal"
            id="name"
            label="First Name"
            type="firstname"
            variant="outlined"
            value={firstName}
            sx={{ width: "49%", marginRight: "10px" }}
            onChange={handleEditFirstNameChange}
          />
          <TextField
            margin="normal"
            id="name"
            label="Last Name"
            type="lastname"
            variant="outlined"
            value={lastName}
            sx={{ width: "49%" }}
            onChange={handleEditLastNameChange}
          />
          <FormControl
            // sx={{ width: "130px", marginTop: "20px", marginRight: "20px" }}
            sx={{ width: "49%", marginRight: "10px", marginTop: "20px" }}
          >
            <InputLabel id="demo-simple-select-label">Role</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={role}
              label="Role"
              onChange={handleRoleChange}
            >
              <MenuItem value={"Regular"}>Regular</MenuItem>
              <MenuItem value={"Prime"}>Prime</MenuItem>
              <MenuItem value={"Internal"}>Internal</MenuItem>
              <MenuItem value={"Admin"}>Admin</MenuItem>
            </Select>
          </FormControl>
          <FormControl
            // sx={{ width: "130px", marginTop: "20px" }}
            sx={{ width: "49%", marginTop: "20px" }}
          >
            <InputLabel id="demo-simple-select-label">Status</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={status}
              label="status"
              onChange={handleActiveChange}
            >
              <MenuItem value={"Active"}>Active</MenuItem>
              <MenuItem value={"Inactive"}>Inactive</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ marginRight: "15px", marginBottom: "10px" }}>
          <Button variant="outlined" onClick={handleUserClose}>Cancel</Button>
          <Button variant="contained" onClick={handleUserConfirm}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------- add user ---------- */}
      <Dialog open={addUserOpen} onClose={handleAddUserClose}>
        <DialogTitle>Add User</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Please fill out user's information here.
          </DialogContentText>
          <TextField
            margin="normal"
            id="name"
            label="Email"
            type="email"
            fullWidth
            variant="outlined"
            value={addEmail}
            onChange={handleAddEmailChange}
          />
          <TextField
            margin="normal"
            id="name"
            label="Username"
            type="username"
            fullWidth
            variant="outlined"
            value={addUsername}
            onChange={handleAddUsernameChange}
          />
          <TextField
            margin="normal"
            id="name"
            label="First Name"
            type="firstname"
            variant="outlined"
            value={addFirstName}
            sx={{ width: "49%", marginRight: "10px" }}
            onChange={handleAddFirstNameChange}
          />
          <TextField
            margin="normal"
            id="name"
            label="Last Name"
            type="lastname"
            variant="outlined"
            value={addLastName}
            sx={{ width: "49%" }}
            onChange={handleAddLastNameChange}
          />
          <FormControl
            fullWidth
            sx={{ marginTop: "20px", marginRight: "20px" }}
          >
            <InputLabel id="demo-simple-select-label">Role</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={addRole}
              label="Role"
              onChange={handleAddRoleChange}
            >
              <MenuItem value={"Regular"}>Regular</MenuItem>
              <MenuItem value={"Prime"}>Prime</MenuItem>
              <MenuItem value={"Internal"}>Internal</MenuItem>
              <MenuItem value={"Admin"}>Admin</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ marginRight: "15px", marginBottom: "10px" }}>
          <Button variant="outlined" onClick={handleAddUserClose}>Cancel</Button>
          <Button variant="contained" onClick={handleAddUserConfirm}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

const mapStateToProps = (state) => {
  return {
    user: state.main.user,
    users: state.m2d.users,
    role: state.m2d.role
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    handleLogout() {
      const action = {
        type: "logout",
      };
      dispatch(action);
    },

    handleAlert(alertType, alertMessage, time = 2500) {
      updateAlert(alertType, alertMessage, time)(dispatch);
    },

    handleUsers(users) {
      updateUsers(users)(dispatch)
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(UserPanel);
